import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Icon, Sparkline, AgentBadge } from "../../components/icons";
import { useMe } from "../../hooks/useMe";
import {
  useRunningPlays,
  useReplanningPlays,
  useMyPendingDecisions,
  useRoadmapInboxCount,
  type PlaySummary,
  type PendingDecisionItem,
} from "../../hooks/useDashboard";
import {
  useWorkspaceEvents,
  type WorkspaceEvent,
} from "../../hooks/useWorkspaceEvents";
import { useQuery } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet } from "../../api/client";

const MAX_ACTIVITY = 50;

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function todayLabel(): string {
  return new Date()
    .toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })
    .toUpperCase();
}

function firstName(displayName: string | undefined, email: string): string {
  // Skip the displayName fallback if it's empty OR if it just echoes the
  // email (some OIDC providers return name=email when no name claim is set).
  const trimmed = displayName?.trim() ?? "";
  if (trimmed.length > 0 && trimmed.toLowerCase() !== email.toLowerCase()) {
    return trimmed.split(/\s+/)[0]!;
  }
  const local = (email.split("@")[0] ?? "there").replace(/[._-]+/g, " ");
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function timeOfEvent(): string {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ActivityItem {
  id: string;
  time: string;
  who: string;
  action: string;
  subject: string;
  agent?: string;
  entering?: boolean;
}

function eventToActivity(e: WorkspaceEvent, idx: number): ActivityItem {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const who =
    (typeof p["actorName"] === "string" && p["actorName"]) ||
    (typeof p["actorKind"] === "string" && p["actorKind"]) ||
    "System";
  const subject =
    (typeof p["title"] === "string" && p["title"]) ||
    (typeof p["playId"] === "string" && `play ${p["playId"]}`) ||
    "";
  const item: ActivityItem = {
    id: `evt-${Date.now()}-${idx}`,
    time: timeOfEvent(),
    who: String(who),
    action: e.type,
    subject: String(subject),
    entering: true,
  };
  if (typeof p["agent"] === "string") item.agent = p["agent"];
  return item;
}

function statusPill(state: string): string {
  if (state === "running") return "green";
  if (state === "paused") return "amber";
  if (state === "replanning") return "purple";
  if (state === "completed") return "blue";
  if (state === "abandoned") return "red";
  return "gray";
}

function readWindow(p: PlaySummary): string {
  const end = p["targetWindowEnd"];
  if (typeof end === "string" && end) {
    try {
      return new Date(end).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return end;
    }
  }
  return "—";
}

function readSteps(p: PlaySummary): { done: number; total: number } {
  const done = typeof p["stepsDone"] === "number" ? (p["stepsDone"] as number) : 0;
  const total = typeof p["stepsTotal"] === "number" ? (p["stepsTotal"] as number) : 0;
  return { done, total };
}

function progressPct(p: PlaySummary): number {
  const v = p["progress"];
  if (typeof v === "number") return Math.max(0, Math.min(100, Math.round(v)));
  const { done, total } = readSteps(p);
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

interface PlayCardProps {
  play: PlaySummary;
  onOpen: (id: string) => void;
}

function PlayCard({ play, onOpen }: PlayCardProps) {
  const status = String(play.state);
  const { done, total } = readSteps(play);
  const replans =
    typeof play["replans"] === "number" ? (play["replans"] as number) : 0;
  const lastEvent =
    typeof play["lastEvent"] === "string"
      ? (play["lastEvent"] as string)
      : "—";
  const live = play.state === "running";
  const owner =
    typeof play["ownerName"] === "string"
      ? (play["ownerName"] as string)
      : (play.ownerUserId ?? "—");
  const agents: string[] = Array.isArray(play["agents"])
    ? ((play["agents"] as unknown[]).filter((a) => typeof a === "string") as string[])
    : [];
  return (
    <div
      className="play-card"
      data-testid={`play-card-${play.id}`}
      onClick={() => onOpen(play.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(play.id);
      }}
    >
      <div className="play-head">
        <div>
          <div className="play-arch">{play.archetype}</div>
          <div className="play-title">{String(play["title"] ?? play.archetype)}</div>
        </div>
        <div className="play-status">
          <span className={`pill ${statusPill(status)}`}>{status}</span>
        </div>
      </div>
      <div className="play-meta">
        <div>
          Steps
          <span className="v">{done}/{total}</span>
        </div>
        <div>
          Window
          <span className="v">{readWindow(play)}</span>
        </div>
        <div>
          Replans
          <span className={`v ${replans > 0 ? "warn" : ""}`}>{replans}</span>
        </div>
      </div>
      <div className="play-progress">
        <div className="bar">
          <span style={{ width: `${progressPct(play)}%` }} />
        </div>
        <div className="row">
          <span>{progressPct(play)}% complete</span>
          <span className="last">
            <span
              className={`live-dot ${live ? "live" : ""}`}
              style={{
                background: live ? "var(--accent-2)" : "var(--text-mute)",
              }}
            />
            {lastEvent}
          </span>
        </div>
      </div>
      <div className="play-foot">
        <span>{owner}</span>
        {agents.length > 0 ? <span>·</span> : null}
        {agents.map((a, i) => (
          <AgentBadge key={`a-${i}`} agent={a} />
        ))}
      </div>
    </div>
  );
}

interface DecisionRowProps {
  decision: PendingDecisionItem;
  onOpen: (playId: string) => void;
}

function DecisionRow({ decision, onOpen }: DecisionRowProps) {
  const kind =
    decision.step.type === "decision"
      ? "decision"
      : decision.play.state === "replanning"
        ? "replan"
        : "default";
  return (
    <div
      className={`decision-row ${kind}`}
      data-testid={`decision-${decision.step.id}`}
    >
      <div className="deco-icon">
        {kind === "replan" ? "⟲" : kind === "decision" ? "●" : "!"}
      </div>
      <div className="deco-body">
        <div className="ttl">{decision.step.title}</div>
        <div className="meta">
          <span>{decision.play.archetype}</span>
          <span className="sep">·</span>
          <span>step {decision.step.ord + 1}</span>
          <span className="sep">·</span>
          <span>{decision.play.workspaceName}</span>
        </div>
      </div>
      <div className="deco-actions">
        <button
          type="button"
          className="btn primary"
          onClick={() => onOpen(decision.play.id)}
        >
          Review
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => onOpen(decision.play.id)}
        >
          Open ↗
        </button>
      </div>
    </div>
  );
}

function DashboardInner() {
  const params = useParams<{ workspaceId: string }>();
  const workspaceId = params.workspaceId ?? "";
  const navigate = useNavigate();
  const me = useMe();

  const runningPlays = useRunningPlays(workspaceId);
  const replanningPlays = useReplanningPlays(workspaceId);
  const decisions = useMyPendingDecisions(workspaceId);
  const roadmapCount = useRoadmapInboxCount(workspaceId);

  const [activity, setActivity] = useState<ActivityItem[]>([]);

  const onEvent = useCallback((e: WorkspaceEvent) => {
    setActivity((prev) => {
      const next = [eventToActivity(e, prev.length), ...prev];
      return next.slice(0, MAX_ACTIVITY);
    });
  }, []);
  const { connectionStatus } = useWorkspaceEvents(workspaceId, onEvent);

  const handleOpenPlay = useCallback(
    (playId: string) => {
      navigate(`/workspaces/${workspaceId}/plays/${playId}`);
    },
    [navigate, workspaceId],
  );

  const greet = greeting();
  const today = todayLabel();
  const personaFirst = me.data?.user
    ? firstName(me.data.user.displayName, me.data.user.email)
    : "there";

  const pendingCount = decisions.data?.items.length ?? 0;
  const replanCount = replanningPlays.data?.items.length ?? 0;
  const inboxCount = roadmapCount.data ?? 0;

  const ledeText = useMemo(() => {
    if (pendingCount > 0) {
      return pendingCount === 1
        ? "1 decision needs you."
        : `${pendingCount} decisions need you.`;
    }
    if (replanCount > 0) {
      return replanCount === 1
        ? "1 replan awaits sign-off."
        : `${replanCount} replans await sign-off.`;
    }
    return "All clear — nothing requires your attention.";
  }, [pendingCount, replanCount]);

  return (
    <div className="main-inner" data-testid="dashboard">
      <div className="greet-row">
        <div className="greet" data-testid="dashboard-greet">
          {greet}, {personaFirst}.
        </div>
        <div className="greet-date">{today}</div>
      </div>

      <div className="subgreet">
        <div className="section-eyebrow">The one thing</div>
        <div className="lede" data-testid="dashboard-lede">
          {ledeText}
        </div>
      </div>

      <div style={{ marginTop: "18px" }}>
        <div className="section-eyebrow">Two things that can wait</div>
        <ul className="subtle-list">
          <li data-testid="lede-decisions-count">
            {pendingCount} decision{pendingCount === 1 ? "" : "s"} in your CxO
            queue
          </li>
          <li data-testid="lede-inbox-count">
            {inboxCount} initiative{inboxCount === 1 ? "" : "s"} in Roadmap
            inbox — needs Plan-it or defer
          </li>
        </ul>
      </div>

      <SpottedTile workspaceId={workspaceId} />


      {/* Pending decisions */}
      <div className="section-h">
        <span className="ttl">Pending decisions</span>
        <span className="meta" data-testid="decisions-meta">
          {pendingCount} awaiting · routed by play
        </span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/inbox`)}
          style={{ cursor: "pointer" }}
        >
          Open queue →
        </span>
      </div>
      <div className="decisions" data-testid="decisions-list">
        {decisions.isLoading ? (
          <div className="decision-row" data-testid="decisions-skeleton">
            <div className="deco-body">
              <div
                className="animate-pulse"
                style={{
                  background: "var(--surface-2)",
                  height: 14,
                  width: 240,
                  borderRadius: 4,
                }}
              />
            </div>
          </div>
        ) : decisions.isError ? (
          <div className="decision-row" data-testid="decisions-error">
            <div className="deco-body">
              <div className="ttl">Couldn't load decisions.</div>
              <div className="meta">{decisions.error?.message ?? "—"}</div>
            </div>
          </div>
        ) : pendingCount === 0 ? (
          <div className="decision-row" data-testid="decisions-empty">
            <div className="deco-body">
              <div className="ttl">No decisions waiting on you.</div>
              <div className="meta">You're caught up.</div>
            </div>
          </div>
        ) : (
          decisions.data!.items.map((d) => (
            <DecisionRow
              key={d.step.id}
              decision={d}
              onOpen={handleOpenPlay}
            />
          ))
        )}
      </div>

      {/* Replans needing approval */}
      <div className="section-h">
        <span className="ttl">Replans needing approval</span>
        <span className="meta" data-testid="replans-meta">
          {replanCount} pending
        </span>
      </div>
      <div className="decisions" data-testid="replans-list">
        {replanningPlays.isLoading ? (
          <div className="decision-row" data-testid="replans-skeleton">
            <div className="deco-body">
              <div
                className="animate-pulse"
                style={{
                  background: "var(--surface-2)",
                  height: 14,
                  width: 240,
                  borderRadius: 4,
                }}
              />
            </div>
          </div>
        ) : replanCount === 0 ? (
          <div className="decision-row" data-testid="replans-empty">
            <div className="deco-body">
              <div className="ttl">No replans awaiting sign-off.</div>
            </div>
          </div>
        ) : (
          replanningPlays.data!.items.map((p) => (
            <div
              key={p.id}
              className="decision-row replan"
              data-testid={`replan-${p.id}`}
            >
              <div className="deco-icon">⟲</div>
              <div className="deco-body">
                <div className="ttl">{String(p["title"] ?? p.archetype)}</div>
                <div className="meta">
                  <span>{p.archetype}</span>
                  <span className="sep">·</span>
                  <span>replanning</span>
                </div>
              </div>
              <div className="deco-actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => handleOpenPlay(p.id)}
                >
                  Review replan
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Running plays */}
      <div className="section-h">
        <span className="ttl">Running plays</span>
        <span className="meta" data-testid="plays-meta">
          {runningPlays.data?.items.length ?? 0} active
        </span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/plays`)}
          style={{ cursor: "pointer" }}
        >
          All plays →
        </span>
      </div>
      <div className="plays" data-testid="plays-list">
        {runningPlays.isLoading ? (
          <div className="play-card" data-testid="plays-skeleton">
            <div
              className="animate-pulse"
              style={{
                background: "var(--surface-2)",
                height: 18,
                width: "60%",
                borderRadius: 4,
              }}
            />
          </div>
        ) : runningPlays.isError ? (
          <div className="play-card" data-testid="plays-error">
            <div className="play-title">Couldn't load plays.</div>
            <div className="play-arch">
              {runningPlays.error?.message ?? "—"}
            </div>
          </div>
        ) : runningPlays.data!.items.length === 0 ? (
          <div className="play-card" data-testid="plays-empty">
            <div className="play-title">No plays are running.</div>
            <div className="play-arch">
              Start a new initiative from the Roadmap inbox.
            </div>
          </div>
        ) : (
          runningPlays.data!.items.map((p) => (
            <PlayCard key={p.id} play={p} onOpen={handleOpenPlay} />
          ))
        )}
      </div>

      {/* KPI deltas — placeholder until governance/velocity ships */}
      <div className="section-h">
        <span className="ttl">KPI deltas — this week</span>
        <span className="meta">Outcome capture · daily cadence</span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/outcomes`)}
          style={{ cursor: "pointer" }}
        >
          Outcomes →
        </span>
      </div>
      <div className="kpis" data-testid="kpis-list">
        <div className="kpi">
          <div className="lbl">Plays running</div>
          <div className="val">{runningPlays.data?.items.length ?? 0}</div>
          <div className="delta up">live</div>
          <div className="spark">
            <Sparkline data={[2, 3, 4, 4, 5, 6]} color="var(--accent)" />
          </div>
        </div>
        <div className="kpi">
          <div className="lbl">Decisions pending</div>
          <div className="val">{pendingCount}</div>
          <div className={`delta ${pendingCount > 0 ? "down" : "up"}`}>
            {pendingCount > 0 ? "↑" : "↓"} vs target
          </div>
          <div className="spark">
            <Sparkline
              data={[1, 2, 3, 2, 1, pendingCount]}
              color="var(--danger)"
            />
          </div>
        </div>
        <div className="kpi">
          <div className="lbl">Inbox queue</div>
          <div className="val">{inboxCount}</div>
          <div className="delta up">tracked</div>
          <div className="spark">
            <Sparkline data={[3, 3, 4, 5, 4, inboxCount]} color="var(--accent)" />
          </div>
        </div>
        <div className="kpi">
          <div className="lbl">Replans awaiting</div>
          <div className="val">{replanCount}</div>
          <div className={`delta ${replanCount > 0 ? "down" : "up"}`}>
            {replanCount > 0 ? "↑" : "↓"} vs target
          </div>
          <div className="spark">
            <Sparkline
              data={[0, 1, 1, 2, 1, replanCount]}
              color="var(--danger)"
            />
          </div>
        </div>
      </div>

      {/* Team activity (live) */}
      <div className="section-h">
        <span className="ttl">Team activity</span>
        <span className="meta" data-testid="activity-meta">
          <span
            style={{
              display: "inline-block",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background:
                connectionStatus === "open"
                  ? "var(--accent-2)"
                  : "var(--text-mute)",
              marginRight: "5px",
              verticalAlign: "middle",
            }}
          />
          {connectionStatus === "open" ? "live" : connectionStatus}
        </span>
      </div>
      <div className="activity" data-testid="activity-list">
        {activity.length === 0 ? (
          <div className="activity-row" data-testid="activity-empty">
            <span className="time">—</span>
            <span>
              <span className="who">No activity yet.</span> Events will appear
              here as work happens.
            </span>
          </div>
        ) : (
          activity.map((a) => (
            <div
              key={a.id}
              className={`activity-row ${a.entering ? "entering" : ""}`}
              data-testid={`activity-${a.id}`}
            >
              <span className="time">{a.time}</span>
              <span>
                <span className="who">{a.who}</span> {a.action}{" "}
                <span style={{ color: "var(--text-3)" }}>{a.subject}</span>
                {a.agent ? (
                  <span className="ag-badge">via {a.agent}</span>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Roadmap inbox preview */}
      <div className="section-h">
        <span className="ttl">Roadmap inbox</span>
        <span className="meta" data-testid="roadmap-preview-meta">
          {inboxCount} initiative{inboxCount === 1 ? "" : "s"} unplanned
        </span>
        <span
          className="right"
          role="link"
          onClick={() => navigate(`/workspaces/${workspaceId}/inbox`)}
          style={{ cursor: "pointer" }}
        >
          Open inbox →
        </span>
      </div>
      <div
        className="callout"
        onClick={() => navigate(`/workspaces/${workspaceId}/inbox`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") navigate(`/workspaces/${workspaceId}/inbox`);
        }}
      >
        <Icon
          name="sparkle"
          className="ic-svg"
          style={{ color: "#c08e1a", marginTop: "1px" }}
        />
        <div>
          <div className="ttl">Promote initiatives from XI to Plays</div>
          <div className="sub">
            Plan-it, defer, or request more context. New plays start in the
            inbox — no orphan plays.
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="dashboard-error">
      <div className="greet-row">
        <span className="greet">Dashboard unavailable</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">ERROR</div>
        <div className="lede">{error.message}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn primary"
          onClick={resetErrorBoundary}
        >
          Reload surface
        </button>
      </div>
    </div>
  );
}

export function Dashboard() {
  // Key on workspaceId so DashboardInner remounts cleanly when the user
  // switches workspaces. Without this, the activity list, SSE
  // subscription, and any other component-local state would carry
  // values from the previous workspace into the new one — which read
  // as "empty until you click Dashboard again" because the queries
  // refetch but the local state lags.
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return (
    <ErrorBoundary FallbackComponent={DashboardErrorFallback}>
      <DashboardInner key={workspaceId ?? "__no_ws__"} />
    </ErrorBoundary>
  );
}

// ─── Spotted tile (Group CXO view) ────────────────────────────────

interface SpotsTileSpot {
  id: string;
  workspaceId: string;
  kind: string;
  title: string;
  impactEstimate: string | null;
  createdAt: string;
}

interface SpotsTileTheme {
  label: string;
  kind: string;
  keyword: string;
  count: number;
  spotIds: string[];
  workspaceCount: number;
}

interface SpotsTile {
  thisWeek: number;
  lastWeek: number;
  topAttention: SpotsTileSpot[];
  silentWorkspaceIds: string[];
  themes?: SpotsTileTheme[];
}

function SpottedTile({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  // Workspace-scoped tile — was previously /api/me/spotted-tile which
  // aggregated spots across every workspace the user belongs to, so a
  // CXO in multiple workspaces saw counts and items from sibling
  // companies. Now keyed on workspaceId so React Query doesn't share
  // cache across workspaces either.
  const q = useQuery<SpotsTile, ApiError>({
    queryKey: ["spotted-tile", workspaceId],
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/spots/tile`),
    enabled: !!workspaceId,
    retry: false,
  });

  if (q.isLoading || q.error || !q.data) return null;

  const tile = q.data;
  const delta = tile.thisWeek - tile.lastWeek;
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "▶";
  const arrowTone = delta > 0 ? "var(--accent, #6366f1)" : delta < 0 ? "var(--text-3, #94a3b8)" : "var(--text-3, #94a3b8)";

  const themes = tile.themes ?? [];
  if (
    tile.thisWeek === 0 &&
    tile.topAttention.length === 0 &&
    tile.silentWorkspaceIds.length === 0 &&
    themes.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="section-h"
      style={{ marginTop: 28, marginBottom: 8 }}
      data-testid="spotted-tile"
    >
      <span className="ttl">Spotted</span>
      <span className="meta">
        <strong>{tile.thisWeek}</strong> this week{" "}
        <span style={{ color: arrowTone }}>
          {arrow} {Math.abs(delta)} vs last
        </span>
        {tile.silentWorkspaceIds.length > 0 && (
          <> · {tile.silentWorkspaceIds.length} workspace{tile.silentWorkspaceIds.length === 1 ? "" : "s"} silent</>
        )}
      </span>
      <span
        className="right"
        role="link"
        onClick={() => navigate(`/workspaces/${workspaceId}/spotted`)}
        style={{ cursor: "pointer" }}
      >
        Open Spotted →
      </span>

      {themes.length > 0 && (
        <div style={{ width: "100%", marginBottom: 8 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: "var(--text-3, #64748b)",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Themes across workspaces
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {themes.map((t) => (
              <span
                key={`${t.kind}-${t.keyword}`}
                className="pill"
                title={`${t.count} ${t.kind.replace(/_/g, " ")} spot${t.count === 1 ? "" : "s"} mentioning '${t.keyword}' across ${t.workspaceCount} workspace${t.workspaceCount === 1 ? "" : "s"}`}
                style={{
                  background: t.workspaceCount > 1 ? "#fef3c7" : "var(--bg-soft, #f1f5f9)",
                  color: t.workspaceCount > 1 ? "#92400e" : "var(--text-2, #475569)",
                  fontSize: 12,
                  padding: "3px 10px",
                  borderRadius: 999,
                }}
              >
                {t.label}{t.workspaceCount > 1 ? ` · ${t.workspaceCount} workspaces` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ width: "100%" }}>
        {tile.topAttention.length === 0 ? (
          <div className="decision-row" data-testid="spotted-tile-empty">
            <div className="deco-body">
              <div className="ttl">Nothing demanding attention right now.</div>
              <div className="meta">
                {tile.silentWorkspaceIds.length > 0
                  ? "But some workspaces haven't reported a spot this week — coverage worth checking."
                  : "All open spots are below the attention threshold."}
              </div>
            </div>
          </div>
        ) : (
          tile.topAttention.map((s) => (
            <div
              key={s.id}
              className="decision-row"
              data-testid={`spotted-tile-row-${s.id}`}
              onClick={() => navigate(`/workspaces/${s.workspaceId}/spotted/${s.id}`)}
              style={{ cursor: "pointer" }}
            >
              <div className="deco-body">
                <div className="ttl">{s.title}</div>
                <div className="meta">
                  {s.kind.replace(/_/g, " ")}
                  {s.impactEstimate ? ` · ${s.impactEstimate} impact` : ""}
                  {" · "}
                  {new Date(s.createdAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

